import { Navigate, useLocation, type Location } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getDefaultRouteForRole } from '@/config/navigation';
import type { StaffRole } from '@/types/auth.types';

// ============================================================================
// TYPES
// ============================================================================

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** If true, requires authentication. If false, redirects authenticated users away */
  requireAuth?: boolean;
  /** Custom redirect path for unauthenticated users */
  redirectTo?: string;
  /**
   * Whitelist of roles that can access this route.
   * If omitted, any authenticated user can access the route.
   * If provided, only users whose staff role is in this list will be allowed;
   * all others are redirected to their role's default route.
   */
  allowedRoles?: StaffRole[];
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * ProtectedRoute Component
 * 
 * Maneja la protección de rutas basada en autenticación y roles.
 * Sin loading screens que interrumpan la experiencia.
 * 
 * - requireAuth=true: Solo usuarios autenticados (rutas privadas)
 * - requireAuth=false: Solo usuarios NO autenticados (rutas de auth como login)
 * - allowedRoles: Restringe el acceso solo a los roles especificados
 * 
 * @example
 * // Ruta protegida (requiere auth)
 * <ProtectedRoute requireAuth={true}>
 *   <DashboardLayout />
 * </ProtectedRoute>
 * 
 * // Ruta protegida con restricción de roles
 * <ProtectedRoute requireAuth={true} allowedRoles={['SUPER_ADMIN', 'SUPPORT_LEAD']}>
 *   <DashboardLayout />
 * </ProtectedRoute>
 * 
 * // Ruta pública (redirige si ya está auth)
 * <ProtectedRoute requireAuth={false}>
 *   <LoginPage />
 * </ProtectedRoute>
 */
export function ProtectedRoute({
  children,
  requireAuth = true,
  redirectTo,
  allowedRoles,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // No loading screen - auth check is instant from localStorage
  // If still loading (first render), prevent flash by not rendering anything
  if (isLoading) {
    return null;
  }

  // CASE 1: Protected route but user is not authenticated
  if (requireAuth && !isAuthenticated) {
    const destination = redirectTo || '/auth/login';
    return (
      <Navigate
        to={destination}
        state={{ from: location }}
        replace
      />
    );
  }

  // CASE 2: Auth route but user is already authenticated
  if (!requireAuth && isAuthenticated) {
    // Redirect to where they came from or dashboard
    const from = (location.state as { from?: Location })?.from?.pathname || '/dashboard';
    return <Navigate to={from} replace />;
  }

  // CASE 2.5: Role mandates TOTP (SUPER_ADMIN / FINANCE) but it isn't enabled
  // yet → HARD-BLOCK. Every protected route bounces to the security page (where
  // the setup wizard lives) until TOTP is enabled. The block lifts automatically
  // once a profile refresh reports totpEnabled = true.
  if (
    requireAuth &&
    isAuthenticated &&
    user?.totpEnforced &&
    !user?.totpEnabled &&
    !location.pathname.startsWith('/account/security')
  ) {
    return <Navigate to="/account/security?setupTotp=1" replace />;
  }

  // CASE 3: Route has role restrictions — check if user's role is allowed
  if (requireAuth && isAuthenticated && allowedRoles && allowedRoles.length > 0) {
    const staffRole = user?.profiles?.staff?.role as StaffRole | undefined;

    if (!staffRole || !allowedRoles.includes(staffRole)) {
      // Se manda a la portada del propio rol en vez de enseñar contenido
      // prohibido. Sin rol se manda a `/`, cuyo `RoleBasedRedirect` **muestra
      // el motivo**: antes iba a `/dashboard`, que redirigía a `/currencies`,
      // que rebotaba aquí otra vez — un bucle infinito (cap. 42).
      const fallback = staffRole ? getDefaultRouteForRole(staffRole) : '/';
      return <Navigate to={fallback} replace />;
    }
  }

  // CASE 4: User has correct auth state (and role, if checked) for this route
  return <>{children}</>;
}

export default ProtectedRoute;
